import React, { FC, useEffect, useMemo, useState } from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';
import moment from 'moment-mini';

import InputSearchOrg from 'components/Organization/InputSearchOrg';

import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputContainer from 'shared/components/FormInputs/InputContainer';
import FiltersHeader from 'components/Organization/Filters/FiltersHeader';
import {
	applyOrganisationToQuery,
	applyStartDayToQuery,
	clearFilterParams,
	parseOrganisationIds,
	parseQuery,
	parseStartDayFromQuery,
	stringifyQuery,
} from './helpers/queryParams';

import DatePickerRangeField from './components/DatePickerRangeField';
import { DateRangeValue, InitialFocusedRange } from 'pages/Admin/SuperAdminDashboard/DateRangePicker';

import styles from './SuperAdminOpportunitiesFiltersNew.module.scss';

type Values = {
	fromDate: Date | null;
	toDate: Date | null;
	organisationId: string | null;
};

const initValues: Values = {
	fromDate: null,
	toDate: null,
	organisationId: null,
};

const OpportunitiesDateRangeValidationSchema = Yup.object().shape({
	fromDate: Yup.date().nullable(),
	toDate: Yup.date()
		.nullable()
		.test({
			name: 'same-or-after-start-date',
			message: 'End date must be after start date',
			test(value) {
				if (!value || !this.parent.fromDate) return true;
				return moment(value, 'DD.MM.YYYY').isSameOrAfter(moment(this.parent.fromDate, 'DD.MM.YYYY'), 'day');
			},
		}),
});

type SuperAdminOpportunitiesFiltersNewProps = {
	enableOrganizationFilter: boolean;
	onClose: () => void;
};

const SuperAdminOpportunitiesFiltersNew: FC<SuperAdminOpportunitiesFiltersNewProps> = ({
	enableOrganizationFilter,
	onClose,
}: SuperAdminOpportunitiesFiltersNewProps) => {
	const location = useLocation();
	const history = useHistory();

	const [initialValues, setInitialValues] = useState<Values>(initValues);

	const externalRange = useMemo<DateRangeValue>(
		() => ({
			startDate: initialValues.fromDate ?? undefined,
			endDate: initialValues.toDate ?? undefined,
		}),
		[initialValues.fromDate, initialValues.toDate]
	);

	useEffect(() => {
		const queryParams = parseQuery(location.search);
		const startDay = parseStartDayFromQuery(queryParams);
		const organisationIds = parseOrganisationIds(queryParams);

		setInitialValues({
			fromDate: startDay?.fromDate || null,
			toDate: startDay?.toDate || null,
			organisationId: organisationIds?.length ? String(organisationIds[0]) : null,
		});
	}, [location.search]);

	const submitClickHandler = (values: Values): void => {
		const parsed = parseQuery(location.search);
		const withDate = applyStartDayToQuery(parsed, values);
		const withOrg = applyOrganisationToQuery(withDate, values.organisationId, enableOrganizationFilter);
		history.push({ search: stringifyQuery(withOrg) });
		onClose();
	};

	const reset = () => {
		setInitialValues(initValues);
		const parsed = parseQuery(location.search);
		history.push({ search: stringifyQuery(clearFilterParams(parsed)) });
	};

	return (
		<div className={styles.filters}>
			<FiltersHeader changeVisibilityFilters={onClose} resetForm={reset} />
			<hr className={styles.separator} />
			<Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={OpportunitiesDateRangeValidationSchema}
				onSubmit={values => submitClickHandler(values)}
			>
				{({ setFieldValue, isValid }) => (
					<Form className={styles.form}>
						{enableOrganizationFilter ? (
							<div className={styles.form__field}>
								<InputContainer isDisableHeight isDisabledMargin>
									<label className={styles.label} htmlFor="organisationId">
										Which organisation?
									</label>
									<Field name="organisationId">
										{({ field }: FieldProps) => (
											<InputSearchOrg
												fieldName={field.name}
												placeholder="Search organization"
												fieldValue={field.value || ''}
												selectHandler={id => setFieldValue(field.name, id)}
											/>
										)}
									</Field>
								</InputContainer>
							</div>
						) : null}

						<Field
							name="fromDate"
							title="From:"
							externalRange={externalRange}
							initialFocusedRange={InitialFocusedRange.StartDate}
							as={DatePickerRangeField}
						/>
						<Field
							name="toDate"
							title="To:"
							externalRange={externalRange}
							initialFocusedRange={InitialFocusedRange.EndDate}
							as={DatePickerRangeField}
						/>

						<div className={styles.actions}>
							<ButtonDefault disabled={!isValid} title="Apply" submitType classList="secondary md" />
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default SuperAdminOpportunitiesFiltersNew;
